import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Calendar, Users, BarChart3, Globe, Database, Zap, X } from "lucide-react";
import { useLanguage } from "@/hooks/useLanguage";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import technologiesBackground from "@/assets/technologies-background-new.jpg";
import SkylineHero from "@/components/SkylineHero";
import ScrollToTop from "@/components/ScrollToTop";

const Technologies = () => {
  const [isFormOpen, setIsFormOpen] = useState(false);
  const { t } = useLanguage();
  const heroAnimation = useScrollAnimation();
  return (
    <div className="min-h-screen bg-white">
      <Header />
      {/* Hero Section */}
      <section className="min-h-[60vh] sm:min-h-[70vh] md:min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-muted/20 to-background relative overflow-hidden">
        {/* Skyline drawing animation — matches hotels hero */}
        <SkylineHero />
        
        {/* Subtle grid pattern */}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,hsl(var(--border))_1px,transparent_1px),linear-gradient(to_bottom,hsl(var(--border))_1px,transparent_1px)] bg-[size:64px_64px] opacity-20"></div>
        <div className="absolute -top-32 sm:-top-40 -right-32 sm:-right-40 w-64 h-64 sm:w-80 sm:h-80 bg-primary/5 rounded-full blur-3xl animate-float"></div>
        <div className="absolute -bottom-32 sm:-bottom-40 -left-32 sm:-left-40 w-64 h-64 sm:w-80 sm:h-80 bg-secondary/5 rounded-full blur-3xl animate-float" style={{ animationDelay: '1.5s' }}></div>
        
        {/* Back button */}
        <Link 
          to="/"
          className="absolute top-4 left-4 sm:top-6 sm:left-6 md:top-8 md:left-8 flex items-center text-muted-foreground hover:text-foreground transition-all duration-300 z-10 hover-scale group bg-background/80 backdrop-blur-sm rounded-full px-3 py-2 border border-border/50"
        >
          <ArrowLeft className="mr-1 sm:mr-2 transition-transform duration-300 group-hover:-translate-x-1" size={16} />
          <span className="text-xs sm:text-sm">{t("common.backToHome")}</span>
        </Link>

        <div 
          ref={heroAnimation.ref as any}
          className={`container mx-auto px-4 sm:px-6 relative z-10 ${heroAnimation.isVisible ? 'animate-fade-in-up' : 'opacity-0'}`}
        >
          <div className="max-w-6xl mx-auto text-center">
            <div className="space-y-4 sm:space-y-6 md:space-y-8">
              <div className="space-y-3 sm:space-y-4 md:space-y-6">
                <h1 className="text-xl sm:text-2xl md:text-4xl lg:text-5xl xl:text-6xl font-light leading-[0.9] tracking-tight text-foreground">
                  Brassbell Technologies
                </h1>
                <div className="w-12 sm:w-16 md:w-20 lg:w-24 h-px bg-gradient-to-r from-primary to-secondary mx-auto"></div>
              </div>
              <p className="text-sm sm:text-base md:text-lg lg:text-xl font-light text-foreground max-w-3xl mx-auto">
                At Brassbell Hospitality Group, technology is not an add-on, it's the backbone of our operations. From property management to guest experience, our fully in-house solutions are built to streamline workflows, maximize revenue, and elevate hospitality to global standards.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Section 1: Built In-House - Left Aligned */}
      <section className="py-12 sm:py-16 md:py-20 lg:py-32 px-4 sm:px-6 bg-background relative overflow-hidden">
        <div className="absolute top-0 left-0 w-1/3 h-full bg-gradient-to-r from-primary/5 to-transparent"></div>
        <div className="container mx-auto">
          <div className="grid lg:grid-cols-12 gap-6 sm:gap-8 md:gap-12 lg:gap-16 items-center">
            <div className="lg:col-span-7">
              <div className="space-y-4 sm:space-y-6 md:space-y-8">
                <div className="flex items-center gap-3 sm:gap-4">
                  <div className="w-8 sm:w-12 md:w-16 h-px bg-gradient-to-r from-primary to-transparent"></div>
                  <span className="text-xs sm:text-sm font-medium text-primary tracking-widest uppercase">In-House Excellence</span>
                </div>
                
                <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl 2xl:text-6xl font-light text-foreground leading-tight">
                  Built In-House,<br />
                  <span className="text-primary">Built for</span><br />
                  Hospitality
                </h2>
                
                <div className="w-12 sm:w-16 md:w-20 lg:w-24 h-px bg-gradient-to-r from-primary to-transparent"></div>
                
                <p className="text-sm sm:text-base md:text-lg text-foreground leading-relaxed max-w-2xl">
                  Unlike off-the-shelf systems, our technology is designed and developed entirely in-house—by hospitality experts for hospitality operators. This gives us unmatched flexibility, security, and the ability to innovate quickly.
                </p>
                
                <p className="text-sm sm:text-base md:text-lg text-foreground leading-relaxed max-w-2xl">
                  Every feature is carefully crafted to solve real-world operational challenges, empowering property managers and hotel operators with tools that simply work.
                </p>
              </div>
            </div>
            
            <div className="lg:col-span-5">
              <div className="relative">
                <div className="absolute -top-4 sm:-top-6 md:-top-8 -right-4 sm:-right-6 md:-right-8 w-24 h-24 sm:w-32 sm:h-32 md:w-40 md:h-40 bg-secondary/10 rounded-full blur-2xl"></div>
                <div className="bg-gradient-to-br from-background to-muted/50 rounded-2xl sm:rounded-3xl p-4 sm:p-6 md:p-8 border border-border/50 backdrop-blur-sm">
                  <div className="space-y-4 sm:space-y-6">
                    <Database className="w-6 h-6 sm:w-8 sm:h-8 md:w-10 md:h-10 lg:w-12 lg:h-12 text-primary" />
                    <h3 className="text-base sm:text-lg md:text-xl font-medium text-foreground">Custom Architecture</h3>
                    <p className="text-xs sm:text-sm md:text-base text-foreground">Designed specifically for hospitality workflows and operational excellence.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section 2: Property Management System - Creative Layout */}
      <section className="py-12 sm:py-16 md:py-20 lg:py-32 px-4 sm:px-6 bg-muted/30 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-2/5 h-full bg-gradient-to-l from-secondary/5 to-transparent"></div>
        <div className="absolute bottom-0 left-1/4 w-24 h-24 sm:w-32 sm:h-32 bg-primary/10 rounded-full blur-2xl"></div>
        
        <div className="container mx-auto">
          <div className="grid lg:grid-cols-12 gap-8 sm:gap-12 md:gap-16 lg:gap-20 items-start">
            <div className="lg:col-span-6">
              <div className="space-y-6 sm:space-y-8">
                <div className="flex items-center gap-3 sm:gap-4">
                  <div className="w-12 sm:w-16 h-px bg-gradient-to-r from-secondary to-transparent"></div>
                  <span className="text-xs sm:text-sm font-medium text-foreground tracking-widest uppercase">Core System</span>
                </div>
                
                <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-light text-foreground leading-tight">
                  Property Management
                  <br />
                  <span className="text-foreground">System</span>
                </h2>
                
                <h3 className="text-lg sm:text-xl md:text-2xl font-light text-foreground">
                  The Core of Modern Hospitality Operations
                </h3>
                
                <div className="w-16 sm:w-24 h-px bg-gradient-to-r from-secondary to-transparent"></div>
                
                <p className="text-sm sm:text-base md:text-lg text-foreground leading-relaxed max-w-2xl">
                  Our intuitive Property Management System centralizes every aspect of property operations. From front-desk management and housekeeping to maintenance, reservations, and customer data, the PMS simplifies complex workflows into a seamless experience.
                </p>
              </div>
            </div>
            
            <div className="lg:col-span-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                {[
                  {
                    icon: Calendar,
                    title: "Reservation & Room Management",
                    description: "Complete booking and room allocation system"
                  },
                  {
                    icon: Users,
                    title: "Guest CRM Integration", 
                    description: "Comprehensive customer relationship management"
                  },
                  {
                    icon: BarChart3,
                    title: "Reporting & Analytics",
                    description: "Real-time insights and performance metrics"
                  },
                  {
                    icon: Zap,
                    title: "Back-office Tools",
                    description: "Streamlined administrative operations"
                  }
                ].map((capability, index) => (
                  <div 
                    key={capability.title}
                    className={`p-4 sm:p-6 bg-background rounded-2xl hover:shadow-lg hover:-translate-y-2 transition-all duration-300 border border-border/50 ${
                      index % 2 === 0 ? 'sm:mt-0' : 'sm:mt-8'
                    }`}
                  >
                    <div className="space-y-3 sm:space-y-4">
                       <div className="w-10 h-10 sm:w-12 sm:h-12 bg-secondary/10 rounded-2xl flex items-center justify-center">
                         <capability.icon className="w-5 h-5 sm:w-6 sm:h-6 text-foreground" />
                       </div>
                      <h4 className="text-sm sm:text-base md:text-lg font-medium text-foreground">{capability.title}</h4>
                      <p className="text-xs sm:text-sm text-foreground leading-relaxed">{capability.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section 3: Channel Manager - Right Aligned */}
      <section className="py-12 sm:py-16 md:py-20 lg:py-32 px-4 sm:px-6 bg-background relative overflow-hidden">
        <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-primary/5 to-transparent"></div>
        <div className="absolute bottom-1/4 right-1/3 w-20 h-20 sm:w-24 sm:h-24 bg-secondary/10 rounded-full blur-xl"></div>
        
        <div className="container mx-auto">
          <div className="grid lg:grid-cols-12 gap-8 sm:gap-12 md:gap-16 items-center">
            <div className="lg:col-span-5 order-first lg:order-first">
              <div className="relative">
                <div className="absolute -top-4 sm:-top-6 -left-4 sm:-left-6 w-24 h-24 sm:w-32 sm:h-32 bg-primary/10 rounded-full blur-2xl"></div>
                <div className="bg-gradient-to-br from-background to-muted/50 rounded-2xl sm:rounded-3xl p-4 sm:p-6 md:p-8 border border-border/50 backdrop-blur-sm">
                  <div className="space-y-4 sm:space-y-6">
                    <Globe className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 text-foreground" />
                    <h3 className="text-base sm:text-lg md:text-xl font-medium text-foreground">Multi-Channel Sync</h3>
                    <p className="text-xs sm:text-sm md:text-base text-foreground">Instant synchronization across all major booking platforms and direct channels.</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="lg:col-span-7 lg:text-right">
              <div className="space-y-6 sm:space-y-8">
                <div className="flex items-center gap-3 sm:gap-4 justify-start lg:justify-end">
                  <span className="text-xs sm:text-sm font-medium text-foreground tracking-widest uppercase">Channel Management</span>
                  <div className="w-12 sm:w-16 h-px bg-gradient-to-l from-secondary to-transparent"></div>
                </div>
                
                <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-light text-foreground leading-tight text-left lg:text-right">
                   <span className="text-foreground">Channel</span><br />
                  Manager
                </h2>
                
                 <h3 className="text-lg sm:text-xl md:text-2xl font-light text-foreground text-left lg:text-right">
                   Seamless Connectivity Across All Booking Platforms
                 </h3>
                
                <div className="flex justify-start lg:justify-end">
                  <div className="w-16 sm:w-24 h-px bg-gradient-to-l from-secondary to-transparent"></div>
                </div>
                
                 <p className="text-sm sm:text-base md:text-lg text-foreground leading-relaxed max-w-2xl lg:ml-auto text-left lg:text-right">
                   Our Channel Manager eliminates the complexity of managing multiple distribution channels. With a single click, availability, rates, and bookings are instantly synchronized across Airbnb, Booking.com, our direct website, and more.
                 </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section 4: Analytics & Reporting - Left Aligned */}
      <section className="py-12 sm:py-16 md:py-20 lg:py-32 px-4 sm:px-6 bg-muted/30 relative overflow-hidden">
        <div className="absolute top-0 left-0 w-2/5 h-full bg-gradient-to-r from-primary/5 to-transparent"></div>
        <div className="absolute top-1/3 right-1/4 w-24 h-24 sm:w-32 sm:h-32 bg-secondary/10 rounded-full blur-2xl"></div>
        
        <div className="container mx-auto">
          <div className="grid lg:grid-cols-12 gap-8 sm:gap-12 md:gap-16 items-center">
            <div className="lg:col-span-7">
              <div className="space-y-6 sm:space-y-8">
                <div className="flex items-center gap-3 sm:gap-4">
                  <div className="w-12 sm:w-16 h-px bg-gradient-to-r from-primary to-transparent"></div>
                  <span className="text-xs sm:text-sm font-medium text-primary tracking-widest uppercase">Data Intelligence</span>
                </div>
                
                <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-light text-foreground leading-tight">
                  Analytics &<br />
                  <span className="text-primary">Reporting</span>
                </h2>
                
                 <h3 className="text-lg sm:text-xl md:text-2xl font-light text-foreground">
                   Data-Driven Decisions, Simplified
                 </h3>
                
                <div className="w-16 sm:w-24 h-px bg-gradient-to-r from-primary to-transparent"></div>
                
                 <p className="text-sm sm:text-base md:text-lg text-foreground leading-relaxed max-w-2xl">
                   We provide operators and landlords with advanced analytics that turn raw data into actionable insights. With real-time dashboards and comprehensive reports, decision-making becomes easier, faster, and more precise.
                 </p>
                 
                 <p className="text-sm sm:text-base md:text-lg text-foreground leading-relaxed max-w-2xl">
                   Our analytics help teams optimize performance, increase efficiency, and maximize revenue potential through intelligent data visualization.
                 </p>
              </div>
            </div>
            
            <div className="lg:col-span-5">
              <div className="relative">
                <div className="absolute -top-4 sm:-top-6 md:-top-8 -right-4 sm:-right-6 md:-right-8 w-32 h-32 sm:w-36 sm:h-36 md:w-40 md:h-40 bg-primary/10 rounded-full blur-2xl"></div>
                <div className="bg-gradient-to-br from-background to-muted/50 rounded-2xl sm:rounded-3xl p-4 sm:p-6 md:p-8 border border-border/50 backdrop-blur-sm">
                  <div className="space-y-4 sm:space-y-6">
                    <BarChart3 className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 text-foreground" />
                    <h3 className="text-base sm:text-lg md:text-xl font-medium text-foreground">Real-time Insights</h3>
                    <p className="text-xs sm:text-sm md:text-base text-foreground">Live dashboards and comprehensive reporting for immediate decision-making.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section 5: Subscription-Based Solutions - Right Aligned */}
      <section className="py-12 sm:py-16 md:py-20 lg:py-32 px-4 sm:px-6 bg-background relative overflow-hidden">
        <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-secondary/5 to-transparent"></div>
        <div className="absolute bottom-1/4 left-1/4 w-24 h-24 sm:w-32 sm:h-32 bg-primary/10 rounded-full blur-2xl"></div>
        
        <div className="container mx-auto">
          <div className="grid lg:grid-cols-12 gap-8 sm:gap-12 md:gap-16 items-center">
            <div className="lg:col-span-5 order-first lg:order-first">
              <div className="relative">
                <div className="absolute -top-4 sm:-top-6 -left-4 sm:-left-6 w-32 h-32 sm:w-36 sm:h-36 md:w-40 md:h-40 bg-secondary/10 rounded-full blur-2xl"></div>
                <div className="bg-gradient-to-br from-background to-muted/50 rounded-2xl sm:rounded-3xl p-4 sm:p-6 md:p-8 border border-border/50 backdrop-blur-sm">
                  <div className="space-y-4 sm:space-y-6">
                    <Zap className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 text-primary" />
                    <h3 className="text-base sm:text-lg md:text-xl font-medium text-foreground">Scalable Solutions</h3>
                    <p className="text-xs sm:text-sm md:text-base text-foreground">Flexible pricing and features that grow with your business needs.</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="lg:col-span-7 lg:text-right">
              <div className="space-y-6 sm:space-y-8">
                <div className="flex items-center gap-3 sm:gap-4 justify-start lg:justify-end">
                  <span className="text-xs sm:text-sm font-medium text-primary tracking-widest uppercase">Business Model</span>
                  <div className="w-12 sm:w-16 h-px bg-gradient-to-l from-primary to-transparent"></div>
                </div>
                
                <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-light text-foreground leading-tight text-left lg:text-right">
                  Subscription-Based<br />
                  <span className="text-primary">Solutions</span>
                </h2>
                
                 <h3 className="text-lg sm:text-xl md:text-2xl font-light text-foreground text-left lg:text-right">
                   Scalable, Flexible, and Future-Ready
                 </h3>
                
                <div className="flex justify-start lg:justify-end">
                  <div className="w-16 sm:w-24 h-px bg-gradient-to-l from-primary to-transparent"></div>
                </div>
                
                 <p className="text-sm sm:text-base md:text-lg text-foreground leading-relaxed max-w-2xl lg:ml-auto text-left lg:text-right">
                   With our subscription-based model, hospitality operators gain access to world-class technology without the burden of heavy upfront investments. Flexible and scalable, our solutions grow with your business, ensuring you stay ahead in an ever-evolving market.
                 </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Closing Section: Vision Statement - Creative Finale */}
      <section className="py-12 sm:py-16 md:py-20 lg:py-32 px-4 sm:px-6 bg-muted/30 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-secondary/5"></div>
        <div className="absolute top-1/4 left-1/3 w-32 h-32 sm:w-40 sm:h-40 md:w-48 md:h-48 bg-primary/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-1/3 right-1/4 w-24 h-24 sm:w-32 sm:h-32 bg-secondary/10 rounded-full blur-2xl animate-pulse" style={{animationDelay: '1s'}}></div>
        
        <div className="container mx-auto relative z-10">
          <div className="grid lg:grid-cols-12 gap-8 sm:gap-12 md:gap-16 items-center">
            <div className="lg:col-span-8 lg:col-start-3">
              <div className="text-center space-y-6 sm:space-y-8">
                <div className="flex items-center gap-3 sm:gap-4 justify-center mb-8 sm:mb-12">
                  <div className="w-12 sm:w-16 md:w-20 h-px bg-gradient-to-r from-transparent via-primary to-transparent"></div>
                  <span className="text-xs sm:text-sm font-medium text-primary tracking-widest uppercase">Our Vision</span>
                  <div className="w-12 sm:w-16 md:w-20 h-px bg-gradient-to-l from-transparent via-primary to-transparent"></div>
                </div>
                
                <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-light text-foreground leading-tight">
                  Where <span className="text-primary">Technology</span><br />
                  Meets <span className="text-foreground">Hospitality</span>
                </h2>
                
                <div className="flex justify-center">
                  <div className="w-20 sm:w-24 md:w-32 h-px bg-gradient-to-r from-transparent via-primary via-secondary to-transparent"></div>
                </div>
                
                 <p className="text-sm sm:text-base md:text-lg lg:text-xl text-foreground leading-relaxed max-w-3xl mx-auto">
                   We don't just provide software, we create the digital infrastructure for the future of hospitality. By combining operational expertise with cutting-edge technology, we empower operators to deliver seamless, memorable experiences at scale.
                 </p>
                
                <div className="pt-6 sm:pt-8">
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 sm:gap-8 max-w-2xl mx-auto">
                    <div className="text-center">
                       <div className="w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 bg-gradient-to-br from-primary/20 to-transparent rounded-2xl mx-auto mb-3 sm:mb-4 flex items-center justify-center">
                         <Globe className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 text-foreground" />
                       </div>
                      <h4 className="text-xs sm:text-sm font-medium text-foreground">Global Standards</h4>
                    </div>
                    <div className="text-center">
                       <div className="w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 bg-gradient-to-br from-secondary/20 to-transparent rounded-2xl mx-auto mb-3 sm:mb-4 flex items-center justify-center">
                         <Zap className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 text-foreground" />
                       </div>
                      <h4 className="text-xs sm:text-sm font-medium text-foreground">Innovation First</h4>
                    </div>
                    <div className="text-center">
                       <div className="w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 bg-gradient-to-br from-primary/20 to-transparent rounded-2xl mx-auto mb-3 sm:mb-4 flex items-center justify-center">
                         <Database className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 text-foreground" />
                       </div>
                      <h4 className="text-xs sm:text-sm font-medium text-foreground">Future Ready</h4>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-12 sm:py-16 md:py-20 lg:py-32 px-4 sm:px-6 bg-gradient-to-br from-background via-muted/30 to-background relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,hsl(var(--primary))_0%,transparent_50%)] opacity-10"></div>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_80%,hsl(var(--secondary))_0%,transparent_50%)] opacity-10"></div>
        
        <div className="container mx-auto relative z-10">
          <div className="max-w-4xl mx-auto">
            <div className="text-center space-y-6 sm:space-y-8 mb-12 sm:mb-16">
              <div className="flex items-center gap-3 sm:gap-4 justify-center">
                <div className="w-12 sm:w-16 md:w-20 h-px bg-gradient-to-r from-transparent via-primary to-transparent"></div>
                <span className="text-xs sm:text-sm font-medium text-primary tracking-widest uppercase">Ready to Transform</span>
                <div className="w-12 sm:w-16 md:w-20 h-px bg-gradient-to-l from-transparent via-primary to-transparent"></div>
              </div>
              
              <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-light text-foreground leading-tight">
                Let's Build the <span className="text-primary">Future</span><br />
                of Your Operations
              </h2>
              
              <div className="flex justify-center">
                <div className="w-20 sm:w-24 md:w-32 h-px bg-gradient-to-r from-transparent via-primary via-secondary to-transparent"></div>
              </div>
              
              <p className="text-sm sm:text-base md:text-lg lg:text-xl text-foreground leading-relaxed max-w-2xl mx-auto">
                Ready to revolutionize your hospitality operations with our cutting-edge technology solutions? 
                Let's discuss how our proprietary PMS, Channel Manager, and Analytics platform can streamline 
                your workflows and maximize your revenue potential.
              </p>
            </div>
            
            <button 
              onClick={() => setIsFormOpen(true)}
              className="bg-gradient-to-br from-background/80 to-muted/50 rounded-2xl sm:rounded-3xl p-6 sm:p-8 md:p-12 backdrop-blur-sm border border-border/50 shadow-2xl hover:shadow-3xl hover:scale-105 transition-all duration-300 cursor-pointer w-full text-left group"
            >
              <div className="space-y-4 sm:space-y-6">
                <h3 className="text-lg sm:text-xl md:text-2xl font-medium text-foreground group-hover:text-primary transition-colors">
                  Transform Your Technology Stack
                </h3>
                <p className="text-sm sm:text-base text-foreground leading-relaxed">
                  Whether you're looking to implement our PMS, integrate our Channel Manager, 
                  or leverage our Analytics platform, our team is here to guide you through 
                  every step of your digital transformation journey.
                </p>
                <div className="space-y-2 sm:space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-primary rounded-full"></div>
                    <span className="text-xs sm:text-sm text-foreground">Personalized technology consultation</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-primary rounded-full"></div>
                    <span className="text-xs sm:text-sm text-foreground">Custom integration planning</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-primary rounded-full"></div>
                    <span className="text-xs sm:text-sm text-foreground">Ongoing support and training</span>
                  </div>
                </div>
                <div className="inline-flex items-center gap-2 text-primary font-medium text-sm sm:text-base group-hover:gap-3 transition-all">
                  <span>Start Your Technology Journey</span>
                  <ArrowLeft className="w-4 h-4 rotate-180 group-hover:translate-x-1 transition-transform" />
                </div>
              </div>
            </button>
          </div>
        </div>
      </section>

      <Footer />
      <ScrollToTop />
      
      {/* Technology Form Modal */}
      {isFormOpen && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-background rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-2xl font-semibold">Transform Your Technology Stack</h3>
                <button 
                  onClick={() => setIsFormOpen(false)}
                  className="text-muted-foreground hover:text-foreground transition-colors p-2"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="grid md:grid-cols-2 gap-6 sm:gap-8 items-start">
                <div className="space-y-4 sm:space-y-6">
                  <p className="text-sm sm:text-base text-foreground leading-relaxed">
                    Whether you're looking to implement our PMS, integrate our Channel Manager, 
                    or leverage our Analytics platform, our team is here to guide you through 
                    every step of your digital transformation journey.
                  </p>
                  <div className="space-y-2 sm:space-y-3">
                    <div className="flex items-center gap-3">
                      <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-primary rounded-full"></div>
                      <span className="text-xs sm:text-sm text-foreground">Personalized technology consultation</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-primary rounded-full"></div>
                      <span className="text-xs sm:text-sm text-foreground">Custom integration planning</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-primary rounded-full"></div>
                      <span className="text-xs sm:text-sm text-foreground">Ongoing support and training</span>
                    </div>
                  </div>
                </div>
                
                <div className="space-y-4 sm:space-y-6">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                    <div className="space-y-1 sm:space-y-2">
                      <label className="text-xs sm:text-sm font-medium text-foreground">Name</label>
                      <input 
                        type="text" 
                        className="w-full px-3 py-2 sm:px-4 sm:py-3 rounded-lg sm:rounded-xl border border-border bg-background/50 backdrop-blur-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary/50 transition-all text-sm"
                        placeholder="Your name"
                      />
                    </div>
                    <div className="space-y-1 sm:space-y-2">
                      <label className="text-xs sm:text-sm font-medium text-foreground">Company</label>
                      <input 
                        type="text" 
                        className="w-full px-3 py-2 sm:px-4 sm:py-3 rounded-lg sm:rounded-xl border border-border bg-background/50 backdrop-blur-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary/50 transition-all text-sm"
                        placeholder="Your company"
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-1 sm:space-y-2">
                    <label className="text-xs sm:text-sm font-medium text-foreground">Email</label>
                    <input 
                      type="email" 
                      className="w-full px-3 py-2 sm:px-4 sm:py-3 rounded-lg sm:rounded-xl border border-border bg-background/50 backdrop-blur-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary/50 transition-all text-sm"
                      placeholder="your@email.com"
                    />
                  </div>
                  
                  <div className="space-y-1 sm:space-y-2">
                    <label className="text-xs sm:text-sm font-medium text-foreground">Technology Interest</label>
                    <select className="w-full px-3 py-2 sm:px-4 sm:py-3 rounded-lg sm:rounded-xl border border-border bg-background/50 backdrop-blur-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary/50 transition-all text-sm">
                      <option value="">Select your primary interest</option>
                      <option value="pms">Property Management System</option>
                      <option value="channel-manager">Channel Manager</option>
                      <option value="analytics">Analytics & Reporting</option>
                      <option value="full-suite">Complete Technology Suite</option>
                      <option value="consultation">Technology Consultation</option>
                    </select>
                  </div>
                  
                  <div className="space-y-1 sm:space-y-2">
                    <label className="text-xs sm:text-sm font-medium text-foreground">Message</label>
                    <textarea 
                      rows={3}
                      className="w-full px-3 py-2 sm:px-4 sm:py-3 rounded-lg sm:rounded-xl border border-border bg-background/50 backdrop-blur-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary/50 transition-all resize-none text-sm"
                      placeholder="Tell us about your current technology challenges and how we can help..."
                    ></textarea>
                  </div>
                  
                  <button className="w-full bg-gradient-to-r from-primary to-secondary text-primary-foreground px-6 py-3 sm:px-8 sm:py-4 rounded-lg sm:rounded-xl font-medium hover:shadow-lg hover:scale-105 transition-all duration-300 text-sm sm:text-base">
                    Start Your Technology Journey
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Technologies;